import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { RogerDocFormTypeCatalogEntry } from '../../services/api/types.gen';
import { SelectableNestedTable } from './SelectableNestedTable';
import { useBespokeTemplatesExport } from './useBespokeTemplatesExport';

export function BespokeTemplatesExportDialog({
  open,
  onOpenChange,
  formTypeCatalog,
  catalogLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
}) {
  const {
    exportPrepLoading,
    exportCandidateRows,
    exportSelectedLeafKeys,
    setExportSelectedLeafKeys,
    exportInProgress,
    exportSelectableUiRows,
    exportAllLeafKeys,
    handleRunBespokeExport,
  } = useBespokeTemplatesExport({
    open,
    formTypeCatalog,
    onClose: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[min(92vh,56rem)] flex flex-col'>
        <DialogHeader>
          <DialogTitle>Export bespoke templates</DialogTitle>
          <DialogDescription>
            Download a JSON bundle of the shared environment-wide bespoke
            catalog. Uncheck rows to exclude templates from the file.
          </DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex flex-1 flex-col gap-2'>
          {!exportPrepLoading &&
          exportCandidateRows.length === 0 &&
          open &&
          formTypeCatalog.length > 0 &&
          !catalogLoading ? (
            <p className='text-sm text-muted-foreground'>
              No bespoke templates match current RogerDoc catalog versions —
              nothing to export.
            </p>
          ) : null}
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-2'
              disabled={
                exportInProgress ||
                exportPrepLoading ||
                exportAllLeafKeys.length === 0
              }
              onClick={() =>
                setExportSelectedLeafKeys(new Set(exportAllLeafKeys))
              }
            >
              Select all rows
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-2'
              disabled={exportInProgress || exportPrepLoading}
              onClick={() => setExportSelectedLeafKeys(new Set())}
            >
              Clear all rows
            </Button>
          </div>
          {exportPrepLoading ? (
            <div
              className='flex min-h-[12rem] max-h-[min(48vh,24rem)] flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground'
              role='status'
              aria-busy='true'
              aria-live='polite'
            >
              <div className='flex items-center gap-2'>
                <Loader2
                  className='h-5 w-5 shrink-0 animate-spin'
                  aria-hidden
                />
                <span>Loading bespoke templates</span>
              </div>
            </div>
          ) : (
            <SelectableNestedTable
              className='min-h-[12rem] max-h-[min(48vh,24rem)] overflow-y-auto overflow-x-auto'
              rows={exportSelectableUiRows}
              selectedKeys={exportSelectedLeafKeys}
              setSelectedKeys={setExportSelectedLeafKeys}
              disabled={
                exportInProgress ||
                catalogLoading ||
                exportSelectableUiRows.length === 0
              }
            />
          )}
        </div>
        <DialogFooter className='shrink-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={exportInProgress}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void handleRunBespokeExport()}
            disabled={
              exportInProgress ||
              exportSelectedLeafKeys.size === 0 ||
              exportPrepLoading
            }
          >
            {exportInProgress && (
              <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
            )}
            Download JSON
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
